/**
 * Schema Generation Progress Component
 *
 * Displays real-time progress during AI schema generation using SSE (Server-Sent Events).
 * Shows stage transitions, progress bar, provider info, and final schema result.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles,
  Database,
  Table2,
  FileCode,
  Brain,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Settings,
  Cpu,
} from 'lucide-react';
import Card, { CardBody } from '../ui/Card';
import { Badge } from '../ui/Badge';

// SSE Event types matching backend
export type SchemaGenerationStage =
  | 'initializing'
  | 'fetching_tables'
  | 'building_prompt'
  | 'calling_llm'
  | 'normalizing'
  | 'generating_ddl'
  | 'complete'
  | 'error';

export interface SchemaProgressEvent {
  stage: SchemaGenerationStage;
  message: string;
  details: Record<string, any>;
  progress_percent: number;
  timestamp: string;
}

export interface SchemaGenerationResult {
  schema_id: string;
  nodes: Array<{
    label: string;
    properties: Array<{
      name: string;
      type: string;
      required?: boolean;
      unique?: boolean;
      indexed?: boolean;
    }>;
    description?: string;
    sourceTables?: string[];
  }>;
  relationships: Array<{
    type: string;
    from: string;
    to: string;
    properties?: Array<{ name: string; type: string }>;
    description?: string;
  }>;
  constraints: Array<{
    name: string;
    label: string;
    properties: string[];
    type: string;
  }>;
  indexes: Array<{
    name: string;
    label: string;
    properties: string[];
    type: string;
  }>;
  cypher_ddl: string;
  metadata: {
    tables_analyzed: number;
    generation_time_seconds: number;
    llm_provider: string;
    total_tokens: number;
    generated_at: string;
  };
}

interface SchemaGenerationProgressProps {
  caseId: string;
  systemId: string;
  selectedTables: string[];
  /** Full table objects (with columns) the user mapped in Step 3. When present the
   *  backend generates directly from these, no Neo4j lookup/enrichment needed. */
  tables?: any[];
  strategicQuestions?: string[];
  businessContext?: string;
  onComplete: (result: SchemaGenerationResult) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

// Stage configuration for display
const stageConfig: Record<SchemaGenerationStage, {
  icon: React.ReactNode;
  title: string;
  color: string;
  bgColor: string;
}> = {
  initializing: {
    icon: <Settings className="w-5 h-5" />,
    title: 'Initializing',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  fetching_tables: {
    icon: <Table2 className="w-5 h-5" />,
    title: 'Analyzing Tables',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  building_prompt: {
    icon: <FileCode className="w-5 h-5" />,
    title: 'Building AI Prompt',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  calling_llm: {
    icon: <Brain className="w-5 h-5" />,
    title: 'AI Processing',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  normalizing: {
    icon: <Database className="w-5 h-5" />,
    title: 'Normalizing Schema',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
  },
  generating_ddl: {
    icon: <Cpu className="w-5 h-5" />,
    title: 'Generating DDL',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  complete: {
    icon: <CheckCircle2 className="w-5 h-5" />,
    title: 'Complete',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  error: {
    icon: <AlertCircle className="w-5 h-5" />,
    title: 'Error',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
};

export const SchemaGenerationProgress: React.FC<SchemaGenerationProgressProps> = ({
  caseId,
  systemId,
  selectedTables,
  tables = [],
  strategicQuestions = [],
  businessContext,
  onComplete,
  onError,
  onCancel,
}) => {
  const [currentEvent, setCurrentEvent] = useState<SchemaProgressEvent | null>(null);
  const [eventHistory, setEventHistory] = useState<SchemaProgressEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  // Start SSE connection
  useEffect(() => {
    const abortController = new AbortController();

    const connectSSE = async () => {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const url = `${apiBaseUrl}/case-analysis/cases/${caseId}/schema/generate-stream`;

        console.log('🔗 SchemaGenerationProgress: Starting SSE connection');
        console.log('   URL:', url);
        console.log('   systemId:', systemId);
        console.log('   selectedTables:', selectedTables);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            system_id: systemId,
            selected_tables: selectedTables,
            // Send the full table objects (with columns) so the backend generates
            // directly from the user's Step 3 mappings — no Neo4j lookup required.
            tables: tables ?? [],
            strategic_questions: strategicQuestions,
            business_context: businessContext,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          console.error('❌ HTTP error:', response.status, response.statusText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('✅ Response OK, setting up stream reader');
        setIsConnected(true);
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          console.error('❌ No response body/reader');
          throw new Error('No response body');
        }

        let buffer = '';
        let chunkCount = 0;

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('📭 Stream complete, received', chunkCount, 'chunks');
            break;
          }

          chunkCount++;
          const chunk = decoder.decode(value, { stream: true });
          console.log(`📦 Chunk ${chunkCount}:`, chunk.substring(0, 100) + (chunk.length > 100 ? '...' : ''));
          buffer += chunk;

          // Process complete SSE messages
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6)) as SchemaProgressEvent;
                console.log('📊 Parsed event:', data.stage, data.progress_percent + '%', data.message);

                setCurrentEvent(data);
                setEventHistory(prev => [...prev, data]);

                // Handle completion
                if (data.stage === 'complete' && data.details?.schema) {
                  onComplete(data.details.schema as SchemaGenerationResult);
                }

                // Handle error
                if (data.stage === 'error') {
                  onError(data.message);
                }
              } catch (e) {
                console.warn('Failed to parse SSE data:', line);
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('SSE connection error:', error);
          onError(error.message || 'Connection failed');
        }
      } finally {
        setIsConnected(false);
      }
    };

    connectSSE();

    return () => {
      abortController.abort();
    };
  }, [caseId, systemId, selectedTables, tables, strategicQuestions, businessContext, onComplete, onError]);

  const config = currentEvent ? stageConfig[currentEvent.stage] : stageConfig.initializing;
  const progressPercent = currentEvent?.progress_percent || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Generating Neo4j Schema
        </h2>
        <p className="text-neutral-600">
          AI is designing your optimal graph database schema
        </p>
      </div>

      {/* Main Progress Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
        <CardBody className="p-6">
          {/* Current Stage */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-12 h-12 rounded-full ${config.bgColor} flex items-center justify-center ${
              currentEvent?.stage === 'calling_llm' ? 'animate-pulse' : ''
            }`}>
              {currentEvent?.stage === 'calling_llm' ? (
                <Loader2 className={`w-6 h-6 ${config.color} animate-spin`} />
              ) : (
                <span className={config.color}>{config.icon}</span>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-neutral-900">
                {currentEvent?.message || 'Starting schema generation...'}
              </h3>
              {currentEvent?.details?.provider && (
                <p className="text-sm text-neutral-600">
                  Provider: <Badge variant="info">{currentEvent.details.provider}</Badge>
                </p>
              )}
            </div>
            {isConnected && (
              <Badge variant="success" className="animate-pulse">
                Live
              </Badge>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-neutral-600">Progress</span>
              <span className="font-medium">{progressPercent}%</span>
            </div>
            <div className="h-3 bg-white rounded-full overflow-hidden border border-purple-200">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Stage Details */}
          {currentEvent?.details && Object.keys(currentEvent.details).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-purple-100">
              {currentEvent.details.table_count !== undefined && (
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-600">{currentEvent.details.table_count}</p>
                  <p className="text-xs text-neutral-500">Tables</p>
                </div>
              )}
              {currentEvent.details.column_count !== undefined && (
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-600">{currentEvent.details.column_count}</p>
                  <p className="text-xs text-neutral-500">Columns</p>
                </div>
              )}
              {currentEvent.details.nodes_count !== undefined && (
                <div className="text-center">
                  <p className="text-xl font-bold text-green-600">{currentEvent.details.nodes_count}</p>
                  <p className="text-xs text-neutral-500">Nodes</p>
                </div>
              )}
              {currentEvent.details.relationships_count !== undefined && (
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-600">{currentEvent.details.relationships_count}</p>
                  <p className="text-xs text-neutral-500">Relationships</p>
                </div>
              )}
              {currentEvent.details.tokens_used !== undefined && (
                <div className="text-center">
                  <p className="text-xl font-bold text-amber-600">{currentEvent.details.tokens_used}</p>
                  <p className="text-xs text-neutral-500">Tokens</p>
                </div>
              )}
              {currentEvent.details.duration_seconds !== undefined && (
                <div className="text-center">
                  <p className="text-xl font-bold text-cyan-600">{currentEvent.details.duration_seconds.toFixed(1)}s</p>
                  <p className="text-xs text-neutral-500">Duration</p>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Stage Timeline */}
      <Card>
        <CardBody className="p-4">
          <h4 className="font-medium text-neutral-900 mb-3">Generation Progress</h4>
          <div className="space-y-2">
            {eventHistory.map((event, index) => {
              const eventConfig = stageConfig[event.stage];
              const isLatest = index === eventHistory.length - 1;

              return (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                    isLatest ? 'bg-neutral-50' : ''
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full ${eventConfig.bgColor} flex items-center justify-center`}>
                    <span className={`${eventConfig.color} scale-75`}>{eventConfig.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${isLatest ? 'font-medium' : 'text-neutral-600'}`}>
                      {event.message}
                    </p>
                  </div>
                  <div className="text-xs text-neutral-400">
                    {event.progress_percent}%
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Cancel Button */}
      {onCancel && currentEvent?.stage !== 'complete' && currentEvent?.stage !== 'error' && (
        <div className="flex justify-center">
          <button
            onClick={onCancel}
            className="text-sm text-neutral-500 hover:text-neutral-700 underline"
          >
            Cancel Generation
          </button>
        </div>
      )}
    </div>
  );
};

export default SchemaGenerationProgress;
